import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { alpha, Box, GlobalStyles, Typography, useTheme } from "@mui/material";
import {
  BodyScrollEvent,
  GetRowHeight,
  GetRowStyle,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridSizeChangedEvent,
  RowClickedEvent,
  RowStyle,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useDebouncedCallback } from "use-debounce";

import { BomOutput, Version } from "fond/types";
import { AgGrid } from "fond/widgets/AgGrid";

import { BomComparisonBanner } from "./BomComparisonBanner";
import { BomComparisonLegend } from "./BomComparisonLegend";
import { buildComparisonRowData, buildGrandTotalRow } from "./buildComparisonRowData";
import {
  buildAutoGroupColumnDef,
  buildComparisonColumnDefs,
  ComparisonGridRow,
  countColId,
  DESCRIPTION_COLUMN_WIDTH,
  getRowId as getComparisonRowId,
  ITEM_COLUMN_WIDTH,
  totalColId,
  VERSION_COLUMN_WIDTH,
} from "./comparisonColumnDefs";

import { AgGridContainer, ComparisonTableRoot, TableToolbar, ToolbarButton } from "./BomComparisonTable.styles";

type BomComparisonTableProps = {
  versions: Version[];
  boms: Record<string, BomOutput>;
  currency?: string;
  systemOfMeasurement: string;
  currentVersionId?: string;
  isFullscreen?: boolean;
  onOpenScenario: (versionId: string) => void;
};

export const BomComparisonTable: React.FC<BomComparisonTableProps> = ({
  versions,
  boms,
  currency,
  systemOfMeasurement,
  currentVersionId,
  isFullscreen,
  onOpenScenario,
}) => {
  const theme = useTheme();
  const gridRef = useRef<AgGridReact>(null);
  const bannerScrollRef = useRef<HTMLDivElement>(null);
  const baselineVersion = versions[0];

  // Bom comparison table columns are flex-sized (see comparisonColumnDefs.tsx).
  // The banner above the grid is a separate DOM structure AG-Grid doesn't lay out, so once the grid
  // resolves actual column widths we mirror them here to keep the banner's title block and version
  // cards aligned with the grid's columns below.
  const [bannerWidths, setBannerWidths] = useState<{ title: number; versions: Record<string, number> }>({
    title: ITEM_COLUMN_WIDTH + DESCRIPTION_COLUMN_WIDTH,
    versions: {},
  });
  const [isBannerCollapsed, setIsBannerCollapsed] = useState(false);

  const rowData = useMemo(() => buildComparisonRowData(versions, boms, systemOfMeasurement), [versions, boms, systemOfMeasurement]);
  const groupCount = useMemo(() => new Set(rowData.map((row) => row.path[0])).size, [rowData]);
  const pinnedBottomRowData = useMemo(() => (baselineVersion ? [buildGrandTotalRow(versions, boms)] : []), [versions, boms, baselineVersion]);

  const columnDefs = useMemo(
    () => (baselineVersion ? buildComparisonColumnDefs({ versions, baselineVersion, currency, theme }) : []),
    [versions, baselineVersion, currency, theme]
  );

  /**
   * Sync the banner's title block and version cards' widths to the grid's actual column widths, so they stay
   * aligned when the grid is resized or columns are pinned to their minimum widths.
   */
  const syncBannerWidths = useCallback(
    (api: GridApi<ComparisonGridRow>) => {
      const itemWidth = api.getColumn("ag-Grid-AutoColumn")?.getActualWidth() ?? ITEM_COLUMN_WIDTH;
      const descriptionWidth = api.getColumn("description")?.getActualWidth() ?? DESCRIPTION_COLUMN_WIDTH;

      setBannerWidths({
        title: itemWidth + descriptionWidth,
        versions: Object.fromEntries(
          versions.map((version) => {
            const quantityWidth = api.getColumn(countColId(version.ID))?.getActualWidth();
            const totalWidth = api.getColumn(totalColId(version.ID))?.getActualWidth();
            return [version.ID, quantityWidth !== undefined && totalWidth !== undefined ? quantityWidth + totalWidth : VERSION_COLUMN_WIDTH];
          })
        ),
      });
    },
    [versions]
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent<ComparisonGridRow>) => {
      syncBannerWidths(event.api);
    },
    [syncBannerWidths]
  );

  const handleGridSizeChanged = useCallback(
    (event: GridSizeChangedEvent<ComparisonGridRow>) => {
      syncBannerWidths(event.api);
    },
    [syncBannerWidths]
  );

  // Column count (and therefore each flex column's actual width) changes whenever the version list
  // changes, without necessarily firing a grid size change - re-sync explicitly once the grid has
  // applied the new columnDefs.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) syncBannerWidths(api);
  }, [columnDefs, syncBannerWidths]);

  // Resize the banner section when the fullscreen mode is toggled.
  const debouncedSyncOnFullscreenChange = useDebouncedCallback(() => {
    const api = gridRef.current?.api;
    if (api) syncBannerWidths(api);
  }, 250);

  useEffect(() => {
    debouncedSyncOnFullscreenChange();
    return () => debouncedSyncOnFullscreenChange.cancel();
  }, [isFullscreen, debouncedSyncOnFullscreenChange]);

  const getRowStyle: GetRowStyle<ComparisonGridRow> = useCallback(
    (params): RowStyle | undefined => {
      //  Different styles for the grand-total row (pinned to the bottom)
      if (params.node.rowPinned === "bottom") {
        return { background: alpha(theme.palette.biarri.primary.midnight, 0.7), color: theme.palette.common.white };
      }
      if (params.node.group) {
        return { cursor: "pointer" };
      }
      return undefined;
    },
    [theme]
  );

  // Clicking anywhere on a group row toggles its expanded state, rather than only the small expand arrow.
  const handleRowClicked = useCallback((event: RowClickedEvent<ComparisonGridRow>) => {
    if (event.node.group) {
      event.node.setExpanded(!event.node.expanded);
    }
  }, []);

  // Different row height for the grand-total row (pinned to the bottom). This is safe alongside
  // autoHeight columns - AG-Grid takes the max of the measured autoHeight and this value per row
  // (see calculateRowHeights in AG-Grid's source), it doesn't disable autoHeight.
  const getRowHeight: GetRowHeight<ComparisonGridRow> = useCallback((params) => (params.node.rowPinned === "bottom" ? 40 : undefined), []);

  // Only the horizontal scroll bar below the comparison table is visible and responsible to control both the table and the banner's horizontal
  // scroll position. Use the grid's onBodyScroll event to sync the banner's scroll position to the grid's horizontal scroll position.
  const handleBodyScroll = useCallback((event: BodyScrollEvent<ComparisonGridRow>) => {
    if (event.direction === "horizontal" && bannerScrollRef.current) {
      bannerScrollRef.current.scrollLeft = event.left;
    }
  }, []);

  const gridOptions: GridOptions<ComparisonGridRow> = useMemo(
    () => ({
      autoGroupColumnDef: buildAutoGroupColumnDef(),
      defaultColDef: { lockPosition: true, resizable: false, suppressHeaderMenuButton: true },
      getRowId: (params) => getComparisonRowId(params.data),
      groupDefaultExpanded: 0,
      rowGroupPanelShow: "never",
      sideBar: false,
      suppressAggFuncInHeader: true,
      suppressAutoSize: true,
      getRowStyle,
      getRowHeight,
      onBodyScroll: handleBodyScroll,
      onGridReady: handleGridReady,
      onGridSizeChanged: handleGridSizeChanged,
      onRowClicked: handleRowClicked,
    }),
    [getRowStyle, getRowHeight, handleBodyScroll, handleGridReady, handleGridSizeChanged, handleRowClicked]
  );

  if (!baselineVersion) return null;

  return (
    <ComparisonTableRoot>
      <GlobalStyles styles={{ ".bom-version-group-header .ag-header-group-cell-label": { justifyContent: "center" } }} />
      <BomComparisonBanner
        ref={bannerScrollRef}
        versions={versions}
        boms={boms}
        currency={currency}
        currentVersionId={currentVersionId}
        onOpenScenario={onOpenScenario}
        bannerWidths={bannerWidths}
        isCollapsed={isBannerCollapsed}
        onToggleCollapsed={() => setIsBannerCollapsed((collapsed) => !collapsed)}
      />
      <TableToolbar>
        <Typography variant="caption" color="text.secondary">
          {groupCount} group{groupCount === 1 ? "" : "s"} · {rowData.length} line item{rowData.length === 1 ? "" : "s"}
        </Typography>
        <Box>
          <ToolbarButton size="small" onClick={() => gridRef.current?.api.expandAll()}>
            Expand all
          </ToolbarButton>
          <ToolbarButton size="small" onClick={() => gridRef.current?.api.collapseAll()}>
            Collapse all
          </ToolbarButton>
        </Box>
      </TableToolbar>
      <AgGridContainer>
        <AgGrid
          ref={gridRef}
          rowData={rowData}
          pinnedBottomRowData={pinnedBottomRowData}
          columnDefs={columnDefs}
          treeData
          getDataPath={(data: ComparisonGridRow) => data.path}
          gridOptions={gridOptions}
          size="compact"
          variant="outlined"
        />
      </AgGridContainer>
      <BomComparisonLegend currency={currency} />
    </ComparisonTableRoot>
  );
};
